/**
 * createAuthBridgeClient — single Supabase Auth client factory shared by
 * every Jubit surface.
 *
 * Storage modes
 * -------------
 *   'localStorage' (default)   Equivalent to the legacy setup. Per-origin.
 *   'cookie'                   Tokens persisted as cookies on the configured
 *                              domain. Use Domain=.jubit.ai to share between
 *                              www.jubit.ai and play.jubit.ai.
 *   'cookie-with-fallback'     Reads cookie first, falls back to localStorage.
 *                              Writes to BOTH (dual-write) so legacy sessions
 *                              continue to work and new sessions are visible
 *                              on both subdomains. Use during the cutover
 *                              window after Phase B; switch to 'cookie' once
 *                              all live sessions have rotated through refresh.
 *   'auto'                     Picks 'cookie-with-fallback' when the current
 *                              hostname ends with `cookieDomain`, else
 *                              'localStorage'. Safe default for shared code
 *                              that can be deployed on multiple domains.
 */
import { createClient } from '@supabase/supabase-js';
import { createCookieStorage } from './cookie-storage.js';
import { createDualStorage } from './dual-storage.js';
const DEFAULT_STORAGE_KEY = 'sb-uqtkxmlpoenphznnapsv-auth-token';
function pickStorageMode(mode, cookieDomain) {
    if (mode === undefined)
        return 'localStorage';
    if (mode !== 'auto')
        return mode;
    // Auto: use cookie-with-fallback when hostname is in the cookieDomain.
    if (!cookieDomain)
        return 'localStorage';
    if (typeof location === 'undefined')
        return 'localStorage';
    const host = location.hostname;
    // Treat ".jubit.ai" the same as "jubit.ai" for the suffix match.
    const suffix = cookieDomain.startsWith('.') ? cookieDomain.slice(1) : cookieDomain;
    if (host === suffix || host.endsWith(`.${suffix}`)) {
        return 'cookie-with-fallback';
    }
    return 'localStorage';
}
function buildStorage(mode, cookieDomain) {
    if (typeof window === 'undefined')
        return undefined;
    const localStorage_ = window.localStorage;
    if (mode === 'localStorage')
        return localStorage_;
    const cookieStore = createCookieStorage({ domain: cookieDomain });
    if (!cookieStore)
        return localStorage_;
    if (mode === 'cookie')
        return cookieStore;
    if (mode === 'cookie-with-fallback') {
        return createDualStorage({
            primary: cookieStore,
            fallback: localStorage_,
            preferPrimary: true,
            dualWrite: true,
        });
    }
    return localStorage_;
}
/**
 * Build a fully configured Supabase Auth client.
 *
 * Pass a Database type parameter to get typed table accessors:
 *   createAuthBridgeClient<Database>({ ... })
 */
export function createAuthBridgeClient(config) {
    const resolvedMode = pickStorageMode(config.storageMode, config.cookieDomain);
    const storage = buildStorage(resolvedMode, config.cookieDomain);
    const storageKey = config.storageKey ?? DEFAULT_STORAGE_KEY;
    return createClient(config.supabaseUrl, config.supabaseAnonKey, {
        auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true,
            flowType: 'pkce',
            storage,
            storageKey,
            debug: config.debug ?? false,
            ...(config.authExtras ?? {}),
        },
    });
}
